import { io, Socket } from 'socket.io-client';
import { Api } from './api';
import { el, svgIcon, ICONS, richTextNodes, richTextSpan } from './dom';
import { EventBatcher } from './events';
import { Identity } from './identity';
import { renderFormFields } from './form-fields';
import { renderQuestion, QuestionStrings } from './question-render';
import {
  ApiError,
  BootstrapData,
  ChatDone,
  ChatForm,
  ChatSurvey,
  MessageAttachment,
  Operator,
  PinnedMessage,
  SurveyAnswer,
} from './types';
import { dlog, pageUrl } from './util';

/** first-letter(s) monogram — the fallback when an operator has no picture */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0].slice(0, 1);
  return parts[0].slice(0, 1) + parts[1].slice(0, 1);
}

/** deterministic monogram background from the name (stable per operator) */
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 58%, 52%)`;
}

/** circular avatar: picture layered over an initials monogram (monogram shows if the image fails) */
function avatarNode(op: Operator, extraClass = ''): HTMLElement {
  const node = el('span', {
    class: `cb-avatar${extraClass ? ' ' + extraClass : ''}`,
    text: initialsOf(op.name),
    attrs: {
      style: `background:${avatarColor(op.name)}`,
      title: op.name,
      'aria-hidden': 'true',
    },
  });
  if (op.avatarUrl) {
    const img = el('img', {
      class: 'cb-avatar-img',
      attrs: { src: op.avatarUrl, alt: '' },
    });
    img.addEventListener('error', () => img.remove()); // fall back to the monogram
    node.appendChild(img);
  }
  return node;
}

interface Strings {
  placeholder: string;
  send: string;
  close: string;
  online: string;
  errorGeneric: string;
  retry: string;
  powered: string;
  pinned: string;
  formSubmit: string;
  formSubmitted: string;
  formRequiredErr: string;
  formChoose: string;
  formTextPlaceholder: string;
  formYes: string;
  formDatePick: string;
  formToday: string;
  formClear: string;
  formPrevMonth: string;
  formNextMonth: string;
  surveySubmit: string;
  surveySubmitted: string;
  surveyRequiredErr: string;
  otherPlaceholder: string;
  followPlaceholder: string;
  optional: string;
  other: string;
  npsLow: string;
  npsHigh: string;
  gateTitle: string;
  gateName: string;
  gatePhone: string;
  gateSubmit: string;
  gateNameErr: string;
  gatePhoneErr: string;
}

const FA: Strings = {
  placeholder: 'پیام خود را بنویسید…',
  send: 'ارسال',
  close: 'بستن گفتگو',
  online: 'پاسخگوی شما هستیم',
  errorGeneric: 'مشکلی پیش آمد؛ لطفاً دوباره تلاش کنید',
  retry: 'تلاش دوباره',
  powered: 'قدرت‌گرفته از چت‌بی',
  pinned: 'پیام سنجاق‌شده',
  formSubmit: 'ثبت فرم',
  formSubmitted: 'فرم ثبت شد',
  formRequiredErr: 'لطفاً به فیلدهای الزامی (*) پاسخ دهید',
  formChoose: 'انتخاب کنید',
  formTextPlaceholder: 'پاسخ خود را بنویسید…',
  formYes: 'بله',
  formDatePick: 'انتخاب تاریخ',
  formToday: 'امروز',
  formClear: 'پاک کردن',
  formPrevMonth: 'ماه قبل',
  formNextMonth: 'ماه بعد',
  surveySubmit: 'ثبت پاسخ‌ها',
  surveySubmitted: 'نظرسنجی ثبت شد',
  surveyRequiredErr: 'لطفاً به سؤال‌های الزامی (*) پاسخ دهید',
  otherPlaceholder: 'توضیح دهید…',
  followPlaceholder: 'نظرتان را بنویسید…',
  optional: '(اختیاری)',
  other: 'سایر',
  npsLow: 'اصلاً پیشنهاد نمی‌کنم',
  npsHigh: 'حتماً پیشنهاد می‌کنم',
  gateTitle: 'برای شروع گفتگو، لطفاً نام و شمارهٔ تماس خود را وارد کنید',
  gateName: 'نام',
  gatePhone: 'شمارهٔ تماس',
  gateSubmit: 'شروع گفتگو',
  gateNameErr: 'لطفاً نام خود را وارد کنید',
  gatePhoneErr: 'شمارهٔ تماس معتبر نیست (مثال: ۰۹۱۲۳۴۵۶۷۸۹)',
};

const EN: Strings = {
  placeholder: 'Type your message…',
  send: 'Send',
  close: 'Close chat',
  online: 'We are online',
  errorGeneric: 'Something went wrong; please try again',
  retry: 'Try again',
  powered: 'Powered by ChatBI',
  pinned: 'Pinned message',
  formSubmit: 'Submit',
  formSubmitted: 'Form submitted',
  formRequiredErr: 'Please fill the required (*) fields',
  formChoose: 'Select…',
  formTextPlaceholder: 'Write your answer…',
  formYes: 'Yes',
  formDatePick: 'Select date',
  formToday: 'Today',
  formClear: 'Clear',
  formPrevMonth: 'Previous month',
  formNextMonth: 'Next month',
  surveySubmit: 'Submit answers',
  surveySubmitted: 'Survey submitted',
  surveyRequiredErr: 'Please answer the required (*) questions',
  otherPlaceholder: 'Please specify…',
  followPlaceholder: 'Tell us more…',
  optional: '(optional)',
  other: 'Other',
  npsLow: 'Not at all likely',
  npsHigh: 'Extremely likely',
  gateTitle: 'To start the chat, please enter your name and phone number',
  gateName: 'Name',
  gatePhone: 'Phone number',
  gateSubmit: 'Start chat',
  gateNameErr: 'Please enter your name',
  gatePhoneErr: 'Please enter a valid phone number',
};

export class ChatPanel {
  private panel: HTMLElement | undefined;
  private msgsEl!: HTMLElement;
  private pinnedEl!: HTMLElement;
  private currentPinnedId?: string;
  private inputEl!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private inputRow!: HTMLElement;
  private gateEl: HTMLElement | undefined;
  private chipsEl: HTMLElement | undefined;
  private isOpen = false;
  private busy = false;
  private sentAnything = false;
  private readonly t: Strings;

  // operator handoff: receive human replies over a socket (with HTTP catch-up on connect)
  private socket?: Socket;
  private cursor?: string; // ISO — catch-up fetches operator replies after this
  private catchUpBusy = false;
  private readonly seenOpIds = new Set<string>();

  // cross-tab sync: a sibling tab wrote the shared transcript while we were busy
  private needsResync = false;

  constructor(
    private readonly host: HTMLElement, // .cb-root inside the shadow
    private readonly boot: BootstrapData,
    private readonly api: Api,
    private readonly ids: Identity,
    private readonly events: EventBatcher,
    private readonly onCloseFocus: () => void,
  ) {
    this.t = boot.site.language === 'fa' ? FA : EN;
    this.sentAnything = ids.getHistory().length > 0;
  }

  get open(): boolean {
    return this.isOpen;
  }

  toggle(): void {
    this.isOpen ? this.close() : this.openPanel();
  }

  openPanel(): void {
    if (this.isOpen) return;
    if (!this.panel) this.build();
    this.panel!.style.display = 'flex';
    this.isOpen = true;
    this.events.track('widget_open');
    this.scrollToEnd();
    // gate first if the merchant requires contact info; otherwise the composer
    (this.gateEl?.querySelector<HTMLInputElement>('input') ?? this.inputEl).focus();
    this.connectRealtime();
  }

  close(): void {
    if (!this.isOpen || !this.panel) return;
    this.panel.style.display = 'none';
    this.isOpen = false;
    this.events.track('widget_close');
    this.onCloseFocus();
  }

  // ---------- construction (lazy: no chat DOM until first open) ----------

  private build(): void {
    const { config } = this.boot;
    const sideStyle =
      config.theme.position === 'bottom-left' ? 'left: var(--cb-margin-x);' : 'right: var(--cb-margin-x);';

    this.msgsEl = el('div', {
      class: 'cb-msgs',
      attrs: { 'aria-live': 'polite', role: 'log' },
    });

    // shared pinned-message bar (hidden until an operator pins something)
    this.pinnedEl = el('button', {
      class: 'cb-pinned',
      attrs: {
        type: 'button',
        style: 'display:none',
        'aria-label': this.t.pinned,
      },
      on: { click: () => this.scrollToPinned() },
    });

    this.inputEl = el('textarea', {
      class: 'cb-input',
      attrs: {
        placeholder: this.t.placeholder,
        rows: '1',
        'aria-label': this.t.placeholder,
      },
      on: {
        keydown: (ev: KeyboardEvent) => {
          if (ev.key === 'Enter' && !ev.shiftKey) {
            ev.preventDefault();
            void this.send(this.inputEl.value);
          }
        },
        input: () => {
          this.inputEl.style.height = 'auto';
          this.inputEl.style.height = Math.min(this.inputEl.scrollHeight, 96) + 'px';
        },
      },
    });

    this.sendBtn = el(
      'button',
      {
        class: 'cb-send',
        attrs: { 'aria-label': this.t.send, type: 'button' },
        on: { click: () => void this.send(this.inputEl.value) },
      },
      [svgIcon(ICONS.send, 18)],
    );

    this.inputRow = el('div', { class: 'cb-inputrow' }, [this.inputEl, this.sendBtn]);

    this.panel = el(
      'div',
      {
        class: 'cb-panel',
        attrs: {
          role: 'dialog',
          'aria-label': config.botName,
          style: sideStyle,
        },
        on: {
          keydown: (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') this.close();
            else if (ev.key === 'Tab') this.trapFocus(ev);
          },
        },
      },
      [
        el('div', { class: 'cb-header' }, [
          ...(config.operators && config.operators.length ? [this.buildRoster(config.operators)] : []),
          el('div', { class: 'cb-title', text: config.botName }, [el('div', { class: 'cb-sub', text: this.t.online })]),
          el(
            'button',
            {
              class: 'cb-iconbtn',
              attrs: { 'aria-label': this.t.close, type: 'button' },
              on: { click: () => this.close() },
            },
            [svgIcon(ICONS.close, 18)],
          ),
        ]),
        this.pinnedEl,
        this.msgsEl,
        this.inputRow,
        el('div', { class: 'cb-powered', text: this.t.powered }),
      ],
    );

    this.renderMessages();
    this.host.appendChild(this.panel);
    this.maybeShowGate();
    this.watchCrossTab();
  }

  /** (re)render the welcome bubble + stored transcript into the message list.
   *  Runs on first build and on cross-tab resync, so it fully rebuilds `msgsEl`
   *  from the stored history (the source of truth) rather than appending. */
  private renderMessages(): void {
    const { config } = this.boot;
    this.removeChips();
    this.msgsEl.replaceChildren();
    // welcome bubble is config, not history — rendered fresh, no feedback row
    this.addBubble('a', config.welcomeMessage);
    for (const m of this.ids.getHistory()) {
      // a stored form/survey prompt re-renders as its card (interactive, or locked if answered)
      if (m.role === 'o' && m.form) {
        this.addFormBubble(m.form, m.mid);
        continue;
      }
      if (m.role === 'o' && m.survey) {
        this.addSurveyBubble(m.survey, m.mid);
        continue;
      }
      const op: Operator | undefined =
        m.role === 'o' ? { name: m.opName || config.botName, avatarUrl: m.opAvatar } : undefined;
      this.addBubble(m.role, m.text, op, m.mid, m.attachments);
    }
    this.sentAnything = this.ids.getHistory().length > 0;
    // suggested-question chips would let a visitor bypass the contact gate (a click
    // sends) — hold them back until the gate is satisfied
    if (!this.sentAnything && !this.needsContactGate() && config.suggestedQuestions.length > 0) {
      this.renderChips();
    }
  }

  /** overlapping avatar stack in the header — the humans behind the bot */
  private buildRoster(operators: Operator[]): HTMLElement {
    const shown = operators.slice(0, 3);
    const stack = el(
      'div',
      {
        class: 'cb-roster',
        attrs: { 'aria-label': shown.map((o) => o.name).join('، ') },
      },
      shown.map((op) => avatarNode(op)),
    );
    if (operators.length > shown.length) {
      stack.appendChild(
        el('span', {
          class: 'cb-avatar cb-avatar-more',
          text: `+${operators.length - shown.length}`,
        }),
      );
    }
    return stack;
  }

  private renderChips(): void {
    this.chipsEl = el(
      'div',
      { class: 'cb-chips' },
      this.boot.config.suggestedQuestions.slice(0, 5).map((q) =>
        el('button', {
          class: 'cb-chip',
          text: q,
          attrs: { type: 'button' },
          on: { click: () => void this.send(q) },
        }),
      ),
    );
    this.msgsEl.appendChild(this.chipsEl);
  }

  private removeChips(): void {
    this.chipsEl?.remove();
    this.chipsEl = undefined;
  }

  // ---------- pre-chat contact gate (config.requireContactInfo) ----------

  /** merchant wants name + phone and this visitor hasn't given them yet */
  private needsContactGate(): boolean {
    return !!this.boot.config.requireContactInfo && !this.ids.hasContact();
  }

  /** if the gate is required, hide the composer and show the name/phone form above it */
  private maybeShowGate(): void {
    if (!this.needsContactGate() || this.gateEl) return;
    this.inputRow.style.display = 'none';
    this.gateEl = this.buildGate();
    this.panel!.insertBefore(this.gateEl, this.inputRow);
  }

  private buildGate(): HTMLElement {
    const nameInput = el('input', {
      class: 'cb-gate-input',
      attrs: {
        type: 'text',
        placeholder: this.t.gateName,
        'aria-label': this.t.gateName,
        autocomplete: 'name',
      },
    }) as HTMLInputElement;
    const phoneInput = el('input', {
      class: 'cb-gate-input',
      attrs: {
        type: 'tel',
        placeholder: this.t.gatePhone,
        'aria-label': this.t.gatePhone,
        inputmode: 'tel',
        autocomplete: 'tel',
        dir: 'ltr',
      },
    }) as HTMLInputElement;
    const errEl = el('div', { class: 'cb-formerr' });
    const submitBtn = el('button', {
      class: 'cb-gate-submit',
      text: this.t.gateSubmit,
      attrs: { type: 'submit' },
    }) as HTMLButtonElement;

    return el(
      'form',
      {
        class: 'cb-gate',
        on: {
          submit: (ev: Event) => {
            ev.preventDefault();
            void this.submitContact(nameInput, phoneInput, errEl, submitBtn);
          },
        },
      },
      [el('div', { class: 'cb-gate-title', text: this.t.gateTitle }), nameInput, phoneInput, errEl, submitBtn],
    );
  }

  /** validate, persist the contact, then reveal the composer */
  private async submitContact(
    nameInput: HTMLInputElement,
    phoneInput: HTMLInputElement,
    errEl: HTMLElement,
    submitBtn: HTMLButtonElement,
  ): Promise<void> {
    const name = nameInput.value.trim();
    const phone = phoneInput.value.trim();
    if (name.length < 2) {
      errEl.textContent = this.t.gateNameErr;
      nameInput.focus();
      return;
    }
    // Iranian mobile — mirror the backend's IdentifyVisitorDto pattern
    if (!/^(?:\+98|0098|0)9\d{9}$/.test(phone)) {
      errEl.textContent = this.t.gatePhoneErr;
      phoneInput.focus();
      return;
    }
    errEl.textContent = '';
    submitBtn.disabled = true;
    try {
      const res = await this.api.identify({
        anonId: this.boot.anonId,
        name,
        phone,
      });
      this.ids.setContact({ name, phone });
      // the server opened the conversation up front — adopt its id so the first message
      // reuses it (no duplicate) and operator replies to this lead stream in right away
      if (res.conversationId && !this.ids.getConversationId()) {
        this.ids.setConversationId(res.conversationId);
        this.connectRealtime();
      }
      this.dismissGate();
    } catch (err) {
      const e = err as ApiError;
      dlog('identify failed', e);
      errEl.textContent = (this.boot.site.language === 'fa' ? e.persianMessage : e.message) || this.t.errorGeneric;
      submitBtn.disabled = false;
    }
  }

  /** contact captured → drop the gate, restore the composer, and release the chips */
  private dismissGate(): void {
    this.gateEl?.remove();
    this.gateEl = undefined;
    this.inputRow.style.display = 'flex';
    if (!this.sentAnything && !this.chipsEl && this.boot.config.suggestedQuestions.length > 0) {
      this.renderChips();
    }
    this.inputEl.focus();
    this.scrollToEnd();
  }

  private trapFocus(ev: KeyboardEvent): void {
    if (!this.panel) return;
    const focusables = this.panel.querySelectorAll<HTMLElement>('button, textarea, [tabindex]');
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = (this.panel.getRootNode() as ShadowRoot).activeElement;
    if (ev.shiftKey && active === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && active === last) {
      ev.preventDefault();
      first.focus();
    }
  }

  // ---------- messages ----------

  private addBubble(
    role: 'v' | 'a' | 'o',
    text: string,
    op?: Operator,
    mid?: string,
    attachments?: MessageAttachment[],
  ): HTMLElement {
    const media = this.buildAttachments(attachments);
    // the caption span is omitted when a media-only message has no text
    const textNodes = text ? [richTextSpan(text)] : [];
    // operator (human) reply: an avatar sits beside a bot-styled bubble, with the name on top
    if (role === 'o') {
      const bubble = el('div', { class: 'cb-msg o' }, [
        ...(op?.name ? [el('span', { class: 'cb-msg-author', text: op.name })] : []),
        ...media,
        ...textNodes,
      ]);
      if (mid) bubble.setAttribute('data-mid', mid);
      const row = el('div', { class: 'cb-msgrow o' }, [avatarNode(op ?? { name: text }), bubble]);
      this.msgsEl.appendChild(row);
      this.scrollToEnd();
      return bubble;
    }

    const bubble = el('div', { class: `cb-msg ${role}` }, [...media, ...textNodes]);
    if (mid) bubble.setAttribute('data-mid', mid);
    this.msgsEl.appendChild(bubble);
    this.scrollToEnd();
    return bubble;
  }

  /** media nodes rendered inside a bubble: image thumb, audio player, or a download link */
  private buildAttachments(attachments?: MessageAttachment[]): HTMLElement[] {
    if (!attachments?.length) return [];
    return attachments
      .filter((a) => a.url)
      .map((a) => {
        if (a.kind === 'image') {
          const img = el('img', {
            class: 'cb-att cb-att-img',
            attrs: { src: a.url!, alt: a.name || '', loading: 'lazy' },
          });
          return el(
            'a',
            {
              class: 'cb-att-link',
              attrs: { href: a.url!, target: '_blank', rel: 'noopener' },
            },
            [img],
          );
        }
        if (a.kind === 'audio') {
          return el('audio', {
            class: 'cb-att cb-att-audio',
            attrs: { src: a.url!, controls: '' },
          });
        }
        return el('a', {
          class: 'cb-att cb-att-file',
          text: `📎 ${a.name || 'فایل'}`,
          attrs: {
            href: a.url!,
            target: '_blank',
            rel: 'noopener',
            download: a.name || '',
          },
        });
      });
  }

  // ---------- operator-sent forms (docs/forms-in-chat.md) ----------

  /** an operator-sent form as an interactive card (or a locked summary once submitted) */
  private addFormBubble(form: ChatForm, mid?: string): HTMLElement {
    const card = el('div', { class: 'cb-msg o cb-formcard' });
    if (mid) card.setAttribute('data-mid', mid);
    card.appendChild(el('div', { class: 'cb-formcard-title', text: form.title }));
    if (form.description) {
      card.appendChild(el('div', { class: 'cb-formcard-desc', text: form.description }));
    }

    if (form.submitted) {
      card.appendChild(this.formDoneNode());
    } else {
      const values: Record<string, unknown> = {};
      const errEl = el('div', { class: 'cb-formerr' });
      const strings = {
        choose: this.t.formChoose,
        textPlaceholder: this.t.formTextPlaceholder,
        yes: this.t.formYes,
        datePick: this.t.formDatePick,
        today: this.t.formToday,
        clear: this.t.formClear,
        prevMonth: this.t.formPrevMonth,
        nextMonth: this.t.formNextMonth,
      };
      card.appendChild(renderFormFields(form.fields, values, () => (errEl.textContent = ''), strings));
      const submitBtn = el('button', {
        class: 'cb-formsubmit',
        text: this.t.formSubmit,
        attrs: { type: 'button' },
        on: {
          click: () => void this.submitFormAnswers(form, values, mid, card, errEl, submitBtn),
        },
      }) as HTMLButtonElement;
      card.appendChild(errEl);
      card.appendChild(submitBtn);
    }

    const row = el('div', { class: 'cb-msgrow o' }, [avatarNode({ name: this.boot.config.botName }), card]);
    this.msgsEl.appendChild(row);
    this.scrollToEnd();
    return card;
  }

  private formDoneNode(): HTMLElement {
    return el('div', { class: 'cb-formcard-done' }, [
      svgIcon(ICONS.check, 15),
      el('span', { text: this.t.formSubmitted }),
    ]);
  }

  /** POST the answers, then lock the card in place */
  private async submitFormAnswers(
    form: ChatForm,
    values: Record<string, unknown>,
    mid: string | undefined,
    card: HTMLElement,
    errEl: HTMLElement,
    submitBtn: HTMLButtonElement,
  ): Promise<void> {
    const missing = form.fields.some((f) => {
      if (!f.isRequired) return false;
      const v = values[f.key];
      return v == null || v === '' || (Array.isArray(v) && v.length === 0);
    });
    if (missing) {
      errEl.textContent = this.t.formRequiredErr;
      return;
    }
    const conversationId = this.ids.getConversationId();
    if (!conversationId || !mid) return;

    submitBtn.disabled = true;
    try {
      await this.api.submitForm({
        conversationId,
        anonId: this.boot.anonId,
        promptMessageId: mid,
        answers: values,
        pageUrl: pageUrl(),
      });
      form.submitted = true;
      this.ids.markFormSubmitted(mid);
      // swap the inputs for the confirmation
      card.querySelectorAll('.cb-form, .cb-formsubmit, .cb-formerr').forEach((n) => n.remove());
      card.appendChild(this.formDoneNode());
      this.scrollToEnd();
    } catch (err) {
      const e = err as ApiError;
      dlog('form submit failed', e);
      errEl.textContent = (this.boot.site.language === 'fa' ? e.persianMessage : e.message) || this.t.errorGeneric;
      submitBtn.disabled = false;
    }
  }

  // ---------- operator-sent surveys (docs/forms-in-chat.md §survey) ----------

  /** an operator-sent survey as an interactive card (or a locked summary once submitted) */
  private addSurveyBubble(survey: ChatSurvey, mid?: string): HTMLElement {
    const card = el('div', { class: 'cb-msg o cb-formcard' });
    if (mid) card.setAttribute('data-mid', mid);
    card.appendChild(el('div', { class: 'cb-formcard-title', text: survey.title }));

    if (survey.submitted) {
      card.appendChild(this.surveyDoneNode());
    } else {
      const answers = new Map<string, SurveyAnswer>();
      const errEl = el('div', { class: 'cb-formerr' });
      const strings: QuestionStrings = {
        otherPlaceholder: this.t.otherPlaceholder,
        textPlaceholder: this.t.formTextPlaceholder,
        followPlaceholder: this.t.followPlaceholder,
        optional: this.t.optional,
        other: this.t.other,
        npsLow: this.t.npsLow,
        npsHigh: this.t.npsHigh,
        choose: this.t.formChoose,
        yes: this.t.formYes,
        datePick: this.t.formDatePick,
        today: this.t.formToday,
        clear: this.t.formClear,
        prevMonth: this.t.formPrevMonth,
        nextMonth: this.t.formNextMonth,
      };
      const questions = el('div', { class: 'cb-survey-questions' });
      for (const q of survey.questions) {
        questions.appendChild(renderQuestion(q, answers, strings));
      }
      card.appendChild(questions);

      const submitBtn = el('button', {
        class: 'cb-formsubmit',
        text: this.t.surveySubmit,
        attrs: { type: 'button' },
        on: {
          click: () => void this.submitSurveyAnswers(survey, answers, mid, card, errEl, submitBtn),
        },
      }) as HTMLButtonElement;
      card.appendChild(errEl);
      card.appendChild(submitBtn);
    }

    const row = el('div', { class: 'cb-msgrow o' }, [avatarNode({ name: this.boot.config.botName }), card]);
    this.msgsEl.appendChild(row);
    this.scrollToEnd();
    return card;
  }

  private surveyDoneNode(): HTMLElement {
    return el('div', { class: 'cb-formcard-done' }, [
      svgIcon(ICONS.check, 15),
      el('span', { text: this.t.surveySubmitted }),
    ]);
  }

  /** POST the answers, then lock the card in place */
  private async submitSurveyAnswers(
    survey: ChatSurvey,
    answers: Map<string, SurveyAnswer>,
    mid: string | undefined,
    card: HTMLElement,
    errEl: HTMLElement,
    submitBtn: HTMLButtonElement,
  ): Promise<void> {
    const missing = survey.questions.some((q) => q.required && !answers.has(q.qid));
    if (missing || answers.size === 0) {
      errEl.textContent = this.t.surveyRequiredErr;
      return;
    }
    const conversationId = this.ids.getConversationId();
    if (!conversationId || !mid) return;

    submitBtn.disabled = true;
    try {
      await this.api.submitSurvey({
        conversationId,
        anonId: this.boot.anonId,
        promptMessageId: mid,
        answers: Array.from(answers.values()),
        pageUrl: pageUrl(),
      });
      survey.submitted = true;
      this.ids.markSurveySubmitted(mid);
      // swap the questions for the confirmation
      card.querySelectorAll('.cb-survey-questions, .cb-formsubmit, .cb-formerr').forEach((n) => n.remove());
      card.appendChild(this.surveyDoneNode());
      this.scrollToEnd();
    } catch (err) {
      const e = err as ApiError;
      dlog('survey submit failed', e);
      errEl.textContent = (this.boot.site.language === 'fa' ? e.persianMessage : e.message) || this.t.errorGeneric;
      submitBtn.disabled = false;
    }
  }

  // ---------- shared pinned message ----------

  /** show / update / hide the pinned bar from the poll's `pinnedMessage` field */
  private renderPinned(pin?: PinnedMessage | null): void {
    if (!this.pinnedEl) return;
    if (!pin) {
      this.pinnedEl.style.display = 'none';
      this.pinnedEl.replaceChildren();
      this.currentPinnedId = undefined;
      return;
    }
    this.currentPinnedId = pin.id;
    this.pinnedEl.replaceChildren(
      el('span', { class: 'cb-pin-ico' }, [svgIcon(ICONS.pin, 14)]),
      el('span', { class: 'cb-pin-body' }, [
        el('span', { class: 'cb-pin-label', text: this.t.pinned }),
        el('span', { class: 'cb-pin-text', text: pin.text }),
      ]),
    );
    this.pinnedEl.style.display = 'flex';
  }

  /** jump to the pinned message if its bubble is in the DOM (tagged with data-mid) */
  private scrollToPinned(): void {
    const id = this.currentPinnedId;
    if (!id) return;
    const target = this.msgsEl.querySelector<HTMLElement>(`[data-mid="${id}"]`);
    if (!target) return;
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.classList.remove('cb-flash');
    void target.offsetWidth; // reflow so a repeat click re-triggers the flash
    target.classList.add('cb-flash');
  }

  private scrollToEnd(): void {
    this.msgsEl.scrollTop = this.msgsEl.scrollHeight;
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.inputEl.disabled = busy;
    this.sendBtn.disabled = busy;
  }

  // ---------- operator handoff: receive human replies over a socket ----------

  /** Connect the socket as soon as the widget mounts — not only once a conversation
   *  exists — so the visitor is reachable for presence and the operator "open widget"
   *  nudge even while browsing with the panel closed. Reconnects are handled by
   *  socket.io; each (re)connect re-joins the rooms and does an HTTP catch-up. */
  connectRealtime(): void {
    if (this.socket) {
      if (this.socket.connected) this.joinAndCatchUp();
      return;
    }

    this.socket = io(`${this.api.socketOrigin()}/chat`, {
      auth: { siteKey: this.api.siteKeyValue() },
      transports: ['websocket', 'polling'],
    });
    this.socket.on('connect', () => this.joinAndCatchUp());
    this.socket.on('message', (m: any) => this.onRemoteMessage(m));
    // operator nudge → pop the panel open for the visitor
    this.socket.on('openWidget', () => this.openPanel());
    this.socket.on('connect_error', (err: Error) => dlog('socket connect_error', err));
  }

  private joinAndCatchUp(): void {
    if (!this.socket) return;
    // always join the visitor room (works before any conversation exists)
    this.socket.emit('joinVisitor', { anonId: this.boot.anonId });

    // and the conversation room, once there is one, so operator replies stream in
    const conversationId = this.ids.getConversationId();
    if (!conversationId) return;
    // catch-up only fetches replies sent after we started watching — history has the rest
    if (!this.cursor) this.cursor = new Date().toISOString();
    this.socket.emit('join', { conversationId, anonId: this.boot.anonId });
    void this.catchUp(); // fill any gap from before/while disconnected
  }

  /** render an operator reply that isn't already on screen */
  private onRemoteMessage(m: any): void {
    if (!m || m.role !== 'operator') return;
    if (m.id && this.seenOpIds.has(m.id)) return;
    if (m.id) this.seenOpIds.add(m.id);
    if (typeof m.createdAt === 'string') this.cursor = m.createdAt;
    const operator: Operator = m.operator ?? { name: '' };
    const mid = typeof m.id === 'string' ? m.id : undefined;

    // operator-sent form → interactive card (not a text bubble)
    if (m.form && Array.isArray(m.form.fields)) {
      const form = m.form as ChatForm;
      this.addFormBubble(form, mid);
      this.ids.appendHistory({
        role: 'o',
        text: m.text ?? '',
        mid,
        form,
        opName: operator.name,
        opAvatar: operator.avatarUrl,
      });
      return;
    }

    // operator-sent survey → interactive card (not a text bubble)
    if (m.survey && Array.isArray(m.survey.questions)) {
      const survey = m.survey as ChatSurvey;
      this.addSurveyBubble(survey, mid);
      this.ids.appendHistory({
        role: 'o',
        text: m.text ?? '',
        mid,
        survey,
        opName: operator.name,
        opAvatar: operator.avatarUrl,
      });
      return;
    }

    if (typeof m.text !== 'string') return;
    const attachments: MessageAttachment[] | undefined = Array.isArray(m.attachments) ? m.attachments : undefined;
    this.addBubble('o', m.text, operator, mid, attachments);
    this.ids.appendHistory({
      role: 'o',
      text: m.text,
      opName: operator.name,
      opAvatar: operator.avatarUrl,
      attachments,
    });
  }

  /** one-shot HTTP fetch of operator replies newer than the cursor (reconnect safety net) */
  private async catchUp(): Promise<void> {
    const conversationId = this.ids.getConversationId();
    if (this.catchUpBusy || !conversationId) return;
    this.catchUpBusy = true;
    try {
      const res = await this.api.pollMessages({
        conversationId,
        anonId: this.boot.anonId,
        after: this.cursor,
      });
      for (const m of res.messages) {
        this.onRemoteMessage({ ...m, role: 'operator' });
      }
      this.renderPinned(res.pinnedMessage ?? null);
    } catch (err) {
      dlog('catch-up failed', err);
    } finally {
      this.catchUpBusy = false;
    }
  }

  // ---------- cross-tab sync: sibling pages share this conversation ----------

  /** The transcript lives in localStorage per site key, so opening the site in a
   *  second page/tab continues the same conversation. But an already-open panel
   *  only reads that store when it builds — without this, a second tab wouldn't
   *  reflect a message sent in the first until it reloads. The `storage` event
   *  fires in the OTHER tabs whenever one writes, letting us resync live.
   *  Best-effort: private modes without a real localStorage never emit it. */
  private watchCrossTab(): void {
    try {
      window.addEventListener('storage', this.onStorage);
    } catch {
      /* never break the host page (util §exception-safe) */
    }
  }

  private readonly onStorage = (ev: StorageEvent): void => {
    if (ev.key !== this.ids.conversationStorageKey || !this.panel) return;
    // a send in flight owns the DOM (pending bubble, streaming deltas); defer the
    // rebuild until it settles so we don't wipe the in-progress reply
    if (this.busy) {
      this.needsResync = true;
      return;
    }
    this.resyncFromStorage();
  };

  /** rebuild the transcript from the shared store and (re)join realtime — a sibling
   *  tab may have just created the conversation this tab didn't have an id for */
  private resyncFromStorage(): void {
    this.needsResync = false;
    this.renderMessages();
    if (this.isOpen) this.connectRealtime();
    this.scrollToEnd();
  }

  // ---------- the send round-trip ----------

  async send(raw: string): Promise<void> {
    const text = raw.trim();
    if (!text || this.busy || !this.panel) return;
    // contact gate not yet satisfied — the composer is hidden, but guard here too
    if (this.needsContactGate()) {
      this.maybeShowGate();
      return;
    }

    this.setBusy(true);
    this.removeChips();
    this.inputEl.value = '';
    this.inputEl.style.height = 'auto';

    const visitorBubble = this.addBubble('v', text);
    this.ids.appendHistory({ role: 'v', text });
    if (!this.sentAnything) {
      this.sentAnything = true;
      this.events.track('chat_started');
    }

    // pending assistant bubble: typing dots until the first delta arrives
    const textSpan = el('span');
    const typing = el('span', { class: 'cb-typing' }, [el('i'), el('i'), el('i')]);
    const pending = el('div', { class: 'cb-msg a' }, [typing, textSpan]);
    this.msgsEl.appendChild(pending);
    this.scrollToEnd();

    let received = '';
    const onDelta = (delta: string) => {
      if (received === '') typing.remove();
      received += delta;
      textSpan.textContent = received;
      this.scrollToEnd();
    };
    const resetPending = () => {
      received = '';
      textSpan.textContent = '';
      if (!typing.parentNode) pending.insertBefore(typing, textSpan);
    };

    const attempt = () =>
      this.api.chatStream(
        {
          message: text,
          anonId: this.boot.anonId,
          conversationId: this.ids.getConversationId(),
          pageUrl: pageUrl(),
        },
        onDelta,
      );

    try {
      let done: ChatDone;
      try {
        done = await attempt();
      } catch (err) {
        const e = err as ApiError;
        if (e.status === 404 && this.ids.getConversationId()) {
          // stale conversation id (another site/session) — drop it, start fresh
          this.ids.clearConversationId();
          resetPending();
          done = await attempt();
        } else if (e.status === 0 && received === '') {
          // network hiccup before anything streamed — one silent retry (doc §4)
          dlog('stream failed pre-delta, retrying once', e);
          resetPending();
          done = await attempt();
        } else {
          throw err;
        }
      }

      this.ids.setConversationId(done.conversationId);
      this.connectRealtime(); // an operator may jump in from here on

      // tag the visitor bubble so a future pin can scroll to it
      if (done.userMessageId) visitorBubble.setAttribute('data-mid', done.userMessageId);

      if (done.handoff || !done.reply) {
        // a human operator has taken over — no bot reply; their answer arrives via poll
        pending.remove();
      } else {
        typing.remove();
        // server copy is authoritative — swap the streamed plaintext for the
        // finished render (citations stripped, URLs turned into links)
        textSpan.replaceChildren(...richTextNodes(done.reply));
        if (done.assistantMessageId) pending.setAttribute('data-mid', done.assistantMessageId);
        this.ids.appendHistory({
          role: 'a',
          text: done.reply,
          mid: done.assistantMessageId,
        });
      }
      this.scrollToEnd();
    } catch (err) {
      const e = err as ApiError;
      dlog('send failed', e);
      pending.remove();
      if (e.errorCode === 'TRIAL_AI_QUOTA_EXHAUSTED') {
        // A page may have stayed open while the final quota was consumed in a
        // different tab/session. Match the next-bootstrap behavior immediately:
        // remove the already-mounted widget instead of leaving a dead composer.
        this.socket?.disconnect();
        const root = this.host.getRootNode();
        if (root instanceof ShadowRoot) root.host.remove();
        return;
      }
      this.renderError(e, text);
    } finally {
      this.setBusy(false);
      this.inputEl.focus();
      // a sibling tab wrote while we were busy — merge its messages in now
      if (this.needsResync) this.resyncFromStorage();
    }
  }

  private renderError(e: ApiError, lastText: string): void {
    // rate-limit & friends: the API's Persian message verbatim (doc §4)
    const label = (this.boot.site.language === 'fa' ? e.persianMessage : e.message) || this.t.errorGeneric;
    const bubble = el('div', { class: 'cb-msg err', text: label });
    // a 429 retry would just burn the visitor's patience inside the same hour
    if (e.status !== 429) {
      bubble.appendChild(
        el('button', {
          class: 'cb-retry',
          text: this.t.retry,
          attrs: { type: 'button' },
          on: {
            click: () => {
              bubble.remove();
              // remove the duplicate visitor bubble the retry would create
              this.ids.dropLastVisitorMessage();
              const bubbles = this.msgsEl.querySelectorAll('.cb-msg.v');
              bubbles[bubbles.length - 1]?.remove();
              void this.send(lastText);
            },
          },
        }),
      );
    }
    this.msgsEl.appendChild(bubble);
    this.scrollToEnd();
  }
}
